<script setup lang="ts">
import { computed, inject } from 'vue'
import SiteEditableText from '../../SiteEditableText.vue'
import { websiteEditableContextKey } from '../../editable-context'
import { extractIframeSource } from '../../iframe'
import type { BookingBlock, BookingProvider } from '../../types'

const props = defineProps<{ block: BookingBlock }>()
const editableContext = inject(websiteEditableContextKey, null)
const isEditable = computed(() => editableContext?.editable.value ?? false)

const providerHosts: Record<Exclude<BookingProvider, 'other'>, string[]> = {
  calendly: ['calendly.com'],
  square: ['square.site', 'squareup.com'],
  acuity: ['acuityscheduling.com'],
  fresha: ['fresha.com'],
  jane: ['janeapp.com'],
  jobber: ['getjobber.com'],
  calcom: ['cal.com'],
}

const bookingUrl = computed(() => {
  const suppliedValue = extractIframeSource(props.block.content.bookingUrl)

  try {
    const url = new URL(suppliedValue)
    return url.protocol === 'https:' ? url.toString() : null
  } catch {
    return null
  }
})

const canEmbed = computed(() => {
  if (!bookingUrl.value || props.block.content.provider === 'other') return false

  const hostname = new URL(bookingUrl.value).hostname
  return providerHosts[props.block.content.provider].some(
    (allowedHost) => hostname === allowedHost || hostname.endsWith(`.${allowedHost}`),
  )
})
</script>

<template>
  <section v-if="block.variant === 'inline'" :id="block.settings.anchorId" class="site:bg-(--site-color-background) site:py-16 site:@min-[40rem]:py-24">
    <div
      class="site-site-container site:gap-8"
      :class="block.settings.schedulerPosition === 'below'
        ? undefined
        : block.settings.schedulerPosition === 'left'
          ? 'site:grid site:items-start site:@min-[64rem]:grid-cols-[minmax(0,1.28fr)_minmax(0,0.72fr)] site:@min-[64rem]:gap-12'
          : 'site:grid site:items-start site:@min-[64rem]:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] site:@min-[64rem]:gap-12'"
    >
      <div
        class="site:pt-3"
        :class="[
          block.settings.schedulerPosition === 'left' ? 'site:@min-[64rem]:order-2' : undefined,
          block.settings.schedulerPosition === 'below' ? 'site:mx-auto site:max-w-3xl site:text-center' : undefined,
        ]"
      >
        <SiteEditableText v-if="block.content.eyebrow" v-model="block.content.eyebrow" as="p" class="site:text-sm site:font-semibold site:text-(--site-color-primary)" />
        <SiteEditableText v-model="block.content.heading" as="h2" class="site-heading site:mt-3 site:text-3xl site:font-bold site:tracking-tight site:@min-[40rem]:text-5xl" />
        <SiteEditableText v-model="block.content.description" as="p" class="site:mt-5 site:text-lg site:leading-8 site:text-(--site-color-muted-text)" />
        <a v-if="bookingUrl" :href="bookingUrl" target="_blank" rel="noopener noreferrer" class="site-site-button site:mt-7">
          {{ block.content.actionLabel }}
        </a>
      </div>

      <div
        class="site:relative site:min-h-[38rem] site:overflow-hidden site:border site:border-(--site-color-border) site:bg-(--site-color-surface) site:shadow-(--site-shadow-md)"
        :class="[
          block.settings.schedulerPosition === 'left' ? 'site:@min-[64rem]:order-1' : undefined,
          block.settings.schedulerPosition === 'below' ? 'site:mx-auto site:mt-10 site:w-full site:max-w-5xl' : undefined,
        ]"
        :style="{ borderRadius: 'var(--site-radius-lg)' }"
      >
        <iframe
          v-if="canEmbed && bookingUrl"
          :src="bookingUrl"
          :title="`${block.content.actionLabel} scheduler`"
          class="site:absolute site:inset-0 site:h-full site:w-full site:border-0"
          :class="isEditable ? 'site:pointer-events-none' : undefined"
          loading="lazy"
          allow="payment"
          referrerpolicy="strict-origin-when-cross-origin"
        />
        <div v-else class="site:absolute site:inset-0 site:grid site:place-items-center site:p-8 site:text-center">
          <div class="site:max-w-sm">
            <p class="site-heading site:text-xl site:font-bold">Online booking</p>
            <p class="site:mt-3 site:leading-7 site:text-(--site-color-muted-text)">Open the secure booking page to view services and available appointment times.</p>
            <a v-if="bookingUrl" :href="bookingUrl" target="_blank" rel="noopener noreferrer" class="site-site-button site:mt-6">{{ block.content.actionLabel }}</a>
            <p v-else class="site:mt-5 site:text-sm site:text-(--site-color-muted-text)">Add a booking link to activate this section.</p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section v-else :id="block.settings.anchorId" class="site:bg-(--site-color-background) site:py-16 site:@min-[40rem]:py-24">
    <div class="site-site-container">
      <div class="site:bg-(--site-color-surface) site:px-7 site:py-12 site:text-center site:shadow-(--site-shadow-md) site:@min-[40rem]:px-12 site:@min-[40rem]:py-16" :style="{ borderRadius: 'var(--site-radius-lg)' }">
        <SiteEditableText v-if="block.content.eyebrow" v-model="block.content.eyebrow" as="p" class="site:text-sm site:font-semibold site:text-(--site-color-primary)" />
        <SiteEditableText v-model="block.content.heading" as="h2" class="site-heading site:mx-auto site:mt-3 site:max-w-3xl site:text-3xl site:font-bold site:tracking-tight site:@min-[40rem]:text-5xl" />
        <SiteEditableText v-model="block.content.description" as="p" class="site:mx-auto site:mt-5 site:max-w-2xl site:text-lg site:leading-8 site:text-(--site-color-muted-text)" />
        <a v-if="bookingUrl" :href="bookingUrl" target="_blank" rel="noopener noreferrer" class="site-site-button site:mt-8">{{ block.content.actionLabel }}</a>
        <p v-else class="site:mt-7 site:text-sm site:text-(--site-color-muted-text)">Add a booking link to activate this section.</p>
      </div>
    </div>
  </section>
</template>
